import React from 'react';
import Layout from '../../components/layout';
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import impact from "../unicorn/impact.png";

const Products = () => {
    // Product data updated to match the image
    const products = [
        // First row - 2 columns, wider cards
        {
            type: "PRIVATE EQUITY",
            product: "Private Equity - Venture Capital Database",
            summary: [
                "Investments since 1998 and exits since 2004 in India-based companies by private equity and venture capital firms.",
                "Includes sub-databases tracking Angel Investing and Incubation/Acceleration activity.",
                "Profiles of PE & VC firms; PE/VC-backed companies and Companies incubated at various Incubators/Accelerators across India.",
                "Shareholding details - Comprehensive & Post Transaction Changes.",
            ],
            link: "/products/pe",
            gridClass: "col-span-1 md:col-span-1",
        },
        {
            type: "FINANCIALS",
            product: "Company Financials Search",
            summary: [
                "Private Company Financials data covering 50k+ companies",
                "Includes Profit & Loss, Balance Sheet, Cash Flow statements and Ratios",
                "Company filings (as reported to MCA)",
                "Index of Charges Data",
                "Various Powerful filters to extract the data",
            ],
            link: "/products/cfs",
            gridClass: "col-span-1 md:col-span-1",
        },
        
        // Second row - 2 columns, wider cards
        {
            type: "MERGER & ACQUISITIONS",
            product: "Merger and Acquisitions Database",
            summary: [
                "Inbound, outbound and domestic deals involving India-based companies.",
                "Enterprise Valuation and multiples",
                "Includes minority stake acquisitions",
            ],
            gridClass: "col-span-1 md:col-span-1",
        },
        {
            type: "REAL ESTATE",
            product: "Private Equity-Real Estate Database",
            summary: [
                "Investments, Exits and M&A in India-based Real Estate companies and projects.",
                "Comprehensive project details",
                "Trends and analytics in PE-RE sector",
            ],
            gridClass: "col-span-1 md:col-span-1",
        },
        
        // Third row - 3 columns, narrower cards
        {
            type: "DIRECTORY",
            product: "Directory of PE-VC Investors",
            summary: [
                {
                    text: "The India Private Equity / Venture Capital Directory",
                    link: "/pedirectory",
                },
                {
                    text: "The India Private Equity-Real Estate Directory",
                    link: "/peredirectory",
                },
                {
                    text: "Directory of Early Stage Investors in India",
                    link: "/pedirectory",
                },
                {
                    text: "Limited Partners Directory",
                    link: "/lpdirectory",
                },
                "Family Office Directory",
            ],
            gridClass: "col-span-1 md:col-span-1 lg:col-span-1",
        },
        {
            type: "NEWSLETTER",
            product: "Deal Digest Newsletter",
            summary: [
                "India's First & Most Exhaustive Transactions Newsletter",
                {
                text:"Premium Daily Edition",
                link: "/newsletters",
                },
                // {
                //     text: "Free Weekly Edition",
                //     link: "/weekly-newsletter",
                // },
            ],
            gridClass: "col-span-1 md:col-span-1 lg:col-span-1",
        },
        {
            type: "UNICORN TRACKER",
            product: "Indian Unicorn Tracker",
            summary: [
                "List of unicorn companies with valuation details",
                "Download the list in excel format",
                {
                    text: "Access the report on unicorn companies",
                    link: "/india_unicorn_report_form",
                },
            ],
            link: "/Indian-Unicorn-Tracker",
            gridClass: "col-span-1 md:col-span-1 lg:col-span-1",
        },
        
        // Fourth row - 2 columns
        {
            type: "LEAGUE TABLES",
            product: "League Tables",
            summary: [
                "The Venture Intelligence League Tables, the first such initiative exclusively tracking transactions involving India-based companies.",
                "Based on value of PE and M&A transactions",
                "Compiled based on transactions submitted by advisory firms",
            ],
            link: "/leagues",
            gridClass: "col-span-1 md:col-span-1",
        },
        {
            type: "OTHER PRODUCTS",
            product: "Other Products & Services",
            summary: [
                // {
                //     text: "MCA Filings - Downloader Software", 
                //     link: "/mcadownloader" 
                // },
                {
                    text: "Just Ask VI - Private Company Financials, Pay per Company",
                    link: "/products/justaskvi",
                },
                {
                    text: "Startup IPO Tracker",
                    link: "/ipo-tracker",
                    },
                    {
                    text: "VC Landscape Report",
                    link: "/india-venture-capital-landscape-report-2025",
                },
                {
                    text: "India AI & Deep Tech Investment Report 2026",
                    link: "/india-ai-deeptech-investments-landscape-report-2026",
                },
                {
                    text: "Series A Landscape Report",
                    link: "/seriesareport",
                },
                {
                    text: "Private Equity Impact Report",
                    link: "/peimpact",
                },
                {
                    text: "Products for Entrepreneurs",
                    link: "/entrepreneurs",
                },
                {
                    text: "VI Gyan - The Venture Intelligence Blog",
                    link: "https://blog.ventureintelligence.com/",
                },
                "Customized research and analytics",
            ],
            gridClass: "col-span-1 md:col-span-1",
        },
    ];

    // Helper function to render summary items with links when needed
    const renderSummaryItem = (point, index) => {
        if (typeof point === "string") {
            return (
                <li key={index} className="flex items-start">
                    <div className="flex-shrink-0 text-[#c59c40] mr-2">•</div>
                    <div>{point}</div>
                </li>
            );
        } else if (point.link) {
            return (
                <li key={index} className="flex items-start">
                    <div className="flex-shrink-0 text-[#c59c40] mr-2">•</div>
                    <div>
                        <a href={point.link} target="_blank" rel="noopener noreferrer" className="text-[#c59c40] hover:text-[#a07924] hover:underline">
                            {point.text}
                        </a>
                    </div>
                </li>
            );
        }
        return null;
    };

    return (
        <Layout>
            <div className="min-h-screen bg-gray-50">
                {/* Hero section with image */}
                <div className="relative h-48 mb-10">
                    <div 
                        className="absolute inset-0 bg-cover bg-center"
                        style={{ 
                        backgroundImage: `url(${impact})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                        }}
                    >
                        <div className="absolute inset-0 bg-black/20"></div>
                    </div>
                    
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4"
                    >
                        <h1 className="text-5xl font-bold text-[#efefef] mb-10 relative group">
                        <span className="relative">
                            PRODUCTS
                        <span className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-[#c59c40] to-[#a07924] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
                        </span>
                        </h1>
                    </motion.div>
                </div>

                {/* Content Container */}
                <div className="max-w-[90rem] mx-auto px-4 pb-16">
                    {/* First Row - 2 columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                        {products.slice(0, 2).map((product, index) => (
                            <motion.div 
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 * index }}
                                className={`bg-white rounded-lg shadow-md p-6 ${product.gridClass}`}
                            >
                                <h2 className="text-xl font-bold text-gray-800 mb-2 border-b border-gray-200 pb-2">
                                    {product.link ? (
                                        <a href={product.link} className="flex items-center hover:text-[#c59c40] transition-colors">
                                            {product.product}
                                            <ExternalLink className="ml-2 h-4 w-4" />
                                        </a>
                                    ) : (
                                        product.product
                                    )}
                                </h2>
                                
                                <ul className="space-y-2 text-gray-700 mt-4">
                                    {product.summary.map((point, idx) => renderSummaryItem(point, idx))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>

                    {/* Second Row - 2 columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                        {products.slice(2, 4).map((product, index) => (
                            <motion.div 
                                key={index + 2}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 * (index + 2) }}
                                className={`bg-white rounded-lg shadow-md p-6 ${product.gridClass}`}
                            >
                                <h2 className="text-xl font-bold text-gray-800 mb-2 border-b border-gray-200 pb-2">
                                    {/* <span className="text-sm font-semibold text-[#c59c40] uppercase tracking-wider block mb-1">
                                        {product.type}
                                    </span> */}
                                    {product.link ? (
                                        <a href={product.link} className="flex items-center hover:text-[#c59c40] transition-colors">
                                            {product.product}
                                            <ExternalLink className="ml-2 h-4 w-4" />
                                        </a>
                                    ) : (
                                        product.product
                                    )}
                                </h2>
                                
                                <ul className="space-y-2 text-gray-700 mt-4">
                                    {product.summary.map((point, idx) => renderSummaryItem(point, idx))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>

                    {/* Third Row - 3 columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                        {products.slice(4, 7).map((product, index) => (
                            <motion.div 
                                key={index + 4}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 * (index + 4) }}
                                className={`bg-white rounded-lg shadow-md p-6 ${product.gridClass}`}
                            >
                                <h2 className="text-xl font-bold text-gray-800 mb-2 border-b border-gray-200 pb-2">
                                    {/* <span className="text-sm font-semibold text-[#c59c40] uppercase tracking-wider block mb-1">
                                        {product.type}
                                    </span> */}
                                    {product.link ? (
                                        <a href={product.link} className="flex items-center hover:text-[#c59c40] transition-colors">
                                            {product.product}
                                            <ExternalLink className="ml-2 h-4 w-4" />
                                        </a>
                                    ) : (
                                        product.product
                                    )}
                                </h2>
                                
                                <ul className="space-y-2 text-gray-700 mt-4">
                                    {product.summary.map((point, idx) => renderSummaryItem(point, idx))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>

                    {/* Fourth Row - 2 columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {products.slice(7, 9).map((product, index) => (
                            <motion.div 
                                key={index + 7}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 * (index + 7) }}
                                className={`bg-white rounded-lg shadow-md p-6 ${product.gridClass}`}
                            >
                                <h2 className="text-xl font-bold text-gray-800 mb-2 border-b border-gray-200 pb-2">

                                    {product.link ? (
                                        <a href={product.link} className="flex items-center hover:text-[#c59c40] transition-colors">
                                            {product.product}
                                            <ExternalLink className="ml-2 h-4 w-4" />
                                        </a>
                                    ) : (
                                        product.product
                                    )}
                                </h2>
                                
                                <ul className="space-y-2 text-gray-700 mt-4">
                                    {product.summary.map((point, idx) => renderSummaryItem(point, idx))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>

                    {/* Demo Request Section */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.8 }}
                        className="mt-12 text-center"
                    >
                        <a
                            href="/trial.html"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex justify-center items-center px-6 py-3 bg-[#c59c40] text-white font-medium rounded hover:bg-[#a07924] transition-colors"
                        >
                            Request a Demo
                        </a>
                    </motion.div>
                </div>
            </div>
        </Layout>
    );
};

export default Products;